/**
 * "Waiting on others" and "Dates at risk" — the two registers.
 *
 * On Tasks they are two badges in the header, on every scope but Calendar;
 * clicking one opens that register in full as a side column, so the list
 * keeps its width until you choose to work through them. Weekly Review shows
 * the top five of each as cards.
 *
 * `riskOf` is the same predicate the task drawer's banner and the Roadmap's
 * dependency arrows use, so the three cannot disagree about what is at risk.
 */

import { card } from "@/components/atoms";
import { itemProjectId } from "@/components/ItemRow";
import { useTaskEditor } from "@/context/TaskEditorContext";
import { riskOf } from "@/lib/deps";
import { parseDate, startOfDay } from "@/lib/format";
import type { Item, Project } from "@/lib/types";

export type RegisterKey = "waiting" | "risk";

/**
 * Both registers in full, worst first: parked work by longest wait, at-risk
 * items by how late the blocker runs. The cards show the top five; the Tasks
 * side panel shows them all.
 */
export function registerLists(open: Item[], byId: Map<string, Item>) {
  const todayMs = startOfDay().getTime();
  const waiting = open
    .filter((i) => (i.waitOn || "").trim())
    .map((i) => {
      const since = parseDate(i.waitSince);
      return { item: i, days: since ? Math.max(0, Math.round((todayMs - since.getTime()) / 86_400_000)) : 0 };
    })
    .sort((a, b) => b.days - a.days);
  // Computed, never stored: an open blocker due on or after this item's date.
  const atRisk = open
    .map((i) => ({ item: i, risk: riskOf(i, byId) }))
    .filter((r): r is { item: Item; risk: NonNullable<ReturnType<typeof riskOf>> } => !!r.risk)
    .sort((a, b) => b.risk.daysLate - a.risk.daysLate);
  return { waiting, atRisk };
}

export function Registers({ open, byId, projectById }: {
  open: Item[];
  byId: Map<string, Item>;
  projectById: Map<string, Project>;
}) {
  const { openItem } = useTaskEditor();
  const lists = registerLists(open, byId);
  const waiting = lists.waiting.slice(0, 5);
  const atRisk = lists.atRisk.slice(0, 5);

  return (
    <>
      <Register title="Waiting on others" count={waiting.length} empty="Nothing is parked with someone else.">
        {waiting.map(({ item, days }) => (
          <RegisterRow
            key={item.id}
            onClick={() => openItem(item)}
            title={item.title}
            meta={[item.waitOn, projectById.get(itemProjectId(item) || "")?.name].filter(Boolean).join(" · ")}
            chip={`${days}d`}
            tone={days >= 7 ? "bad" : "warn"}
          />
        ))}
      </Register>

      <Register title="Dates at risk" count={atRisk.length} empty="No blocked item is threatening a date.">
        {atRisk.map(({ item, risk }) => (
          <RegisterRow
            key={item.id}
            onClick={() => openItem(item)}
            title={item.title}
            meta={risk.message}
            chip={risk.daysLate > 0 ? `${risk.daysLate}d` : undefined}
            tone={risk.daysLate >= 7 ? "bad" : "warn"}
          />
        ))}
      </Register>
    </>
  );
}

/**
 * The registers as two header badges on Tasks: a count, red once anything has
 * waited or slipped a week, amber otherwise, muted at zero. Clicking one opens
 * that register in the side panel; clicking the open one closes it.
 */
export function RegisterBadges({ open, byId, active, onToggle }: {
  open: Item[];
  byId: Map<string, Item>;
  active: RegisterKey | null;
  onToggle: (key: RegisterKey) => void;
}) {
  const { waiting, atRisk } = registerLists(open, byId);
  const badges: { key: RegisterKey; label: string; count: number; worst: number }[] = [
    { key: "waiting", label: "Waiting", count: waiting.length, worst: waiting[0]?.days ?? 0 },
    { key: "risk", label: "At risk", count: atRisk.length, worst: atRisk[0]?.risk.daysLate ?? 0 },
  ];
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      {badges.map((b) => {
        const tone = b.count === 0 ? null : b.worst >= 7 ? "bad" : "warn";
        const on = active === b.key;
        return (
          <button key={b.key} onClick={() => onToggle(b.key)} aria-pressed={on}
            title={on ? `Close ${b.label.toLowerCase()}` : `Show ${b.label.toLowerCase()}`}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 11px", borderRadius: 999,
              fontSize: 12.5, fontWeight: "var(--fw-medium)", fontFamily: "inherit", cursor: "pointer",
              border: `1px solid ${on ? "currentColor" : "transparent"}`,
              background: tone === "bad" ? "var(--badTint)" : tone === "warn" ? "var(--warnTint)" : "var(--tint2)",
              color: tone === "bad" ? "var(--bad)" : tone === "warn" ? "var(--warn)" : "var(--muted)",
            }}>
            {b.label}<span className="mono" style={{ fontSize: 11.5 }}>{b.count}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * One register in full, as Tasks' side column: every item, not the top five,
 * so you can work down the list. A row opens the task; the switch flips to the
 * other register without closing the column.
 */
export function RegisterPanel({ open, byId, projectById, active, onSelect, onClose }: {
  open: Item[];
  byId: Map<string, Item>;
  projectById: Map<string, Project>;
  active: RegisterKey;
  onSelect: (key: RegisterKey) => void;
  onClose: () => void;
}) {
  const { openItem } = useTaskEditor();
  const { waiting, atRisk } = registerLists(open, byId);
  const tabs: [RegisterKey, string, number][] = [["waiting", "Waiting on others", waiting.length], ["risk", "Dates at risk", atRisk.length]];
  const empty = active === "waiting" ? "Nothing is parked with someone else." : "No blocked item is threatening a date.";
  const count = active === "waiting" ? waiting.length : atRisk.length;

  return (
    <div style={{ ...card, minWidth: 0, padding: "10px 14px 12px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 8 }}>
        {tabs.map(([key, label, n]) => (
          <button key={key} onClick={() => onSelect(key)} style={{
            padding: "5px 9px", borderRadius: 7, border: "none", cursor: "pointer", fontFamily: "inherit",
            fontSize: 12.5, fontWeight: "var(--fw-title)", whiteSpace: "nowrap",
            background: active === key ? "var(--tint2)" : "transparent",
            color: active === key ? "var(--ink)" : "var(--muted)",
          }}>{label} <span className="mono" style={{ fontSize: 11, color: "var(--muted2)" }}>{n}</span></button>
        ))}
        <button onClick={onClose} aria-label="Close panel" style={{
          marginLeft: "auto", flex: "none", width: 26, height: 26, borderRadius: 999, border: "none",
          background: "transparent", color: "var(--muted)", fontSize: 17, lineHeight: 1, cursor: "pointer",
        }}>×</button>
      </div>
      {count === 0 && <div style={{ fontSize: 12.5, color: "var(--muted2)", padding: "4px 0" }}>{empty}</div>}
      <div style={{ display: "flex", flexDirection: "column" }}>
        {active === "waiting" ? waiting.map(({ item, days }) => (
          <RegisterRow key={item.id} onClick={() => openItem(item)} title={item.title}
            meta={[item.waitOn, projectById.get(itemProjectId(item) || "")?.name].filter(Boolean).join(" · ")}
            chip={`${days}d`} tone={days >= 7 ? "bad" : "warn"} />
        )) : atRisk.map(({ item, risk }) => (
          <RegisterRow key={item.id} onClick={() => openItem(item)} title={item.title} meta={risk.message}
            chip={risk.daysLate > 0 ? `${risk.daysLate}d` : undefined} tone={risk.daysLate >= 7 ? "bad" : "warn"} />
        ))}
      </div>
    </div>
  );
}

/** A register as a card, always open: Weekly Review's top five. */
function Register({ title, count, empty, children }: {
  title: string; count: number; empty: string; children: React.ReactNode;
}) {
  return (
    <div style={{ ...card, minWidth: 0, padding: "12px 14px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: count ? 8 : 0 }}>
        <span style={{ fontSize: "var(--fs-panel)", fontWeight: "var(--fw-title)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</span>
        <span className="mono" style={{ marginLeft: "auto", fontSize: 11.5, color: "var(--muted2)" }}>{count}</span>
      </div>
      {count === 0
        ? <div style={{ fontSize: 12.5, color: "var(--muted2)" }}>{empty}</div>
        : <div style={{ display: "flex", flexDirection: "column" }}>{children}</div>}
    </div>
  );
}

function RegisterRow({ title, meta, chip, tone, onClick }: {
  title: string; meta: string; chip?: string; tone: "warn" | "bad"; onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left",
        padding: "7px 0", border: "none", borderTop: "1px solid var(--tint2)",
        background: "none", cursor: "pointer",
      }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: "block", fontSize: 13, fontWeight: "var(--fw-title)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</span>
        <span style={{ display: "block", fontSize: 11.5, color: "var(--muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{meta}</span>
      </span>
      {chip && (
        <span className="mono" style={{
          flex: "none", padding: "3px 9px", borderRadius: 999, fontSize: 11,
          background: tone === "bad" ? "var(--badTint)" : "var(--warnTint)",
          color: tone === "bad" ? "var(--bad)" : "var(--warn)",
        }}>{chip}</span>
      )}
    </button>
  );
}
